import {EventEmitter, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Account, AccountType, isAccountType} from '../objects/Account';
import {CurrencyService} from './CurrencyService';
import {Decimal as DecimalJS} from 'decimal.js';
import {BehaviorSubject, Observable, skip} from 'rxjs';
import {filter, map, tap} from 'rxjs/operators';
import {SocketNamespaceServiceBase} from '~local/app/core/socket/SocketNamespaceServiceBase';
import {ensureCurrencyRate} from '~local/app/shared/objects/Currency';
import {AuthenticationService} from '~local/app/core/services/authentication.service';
import {Transaction} from '~local/app/shared/objects/Transaction';

export type AccountResponse = Exclude<Account, 'createdDate' | 'updatedDate'> & {
    createdDate: string,
    updatedDate: string,
};

export function isAccountResponse($: any): $ is AccountResponse {
    return typeof $ === 'object'
        && $ !== null
        && isAccountType($.type);
}

export function accountResponseToAccount($response: Partial<AccountResponse>): Partial<Account> {
    return Object.keys($response).reduce(($acc, $responseKey) => {
        if ($responseKey === 'createdDate' || $responseKey === 'updatedDate') {
            const dateParsed = Date.parse($response[$responseKey]);
            return {
                ...$acc,
                [$responseKey]: new Date(isNaN(dateParsed) ? 0 : dateParsed),
            };
        }

        return {
            ...$acc,
            [$responseKey]: $response[$responseKey]
        };
    }, <Partial<Account>>{});
}

export enum AccountCreationManualApprovalStatus {
    CREATED = 'CREATED',
    APPROVED = 'APPROVED'
}

export function isAccountCreationManualApprovalStatus($: any): $ is AccountCreationManualApprovalStatus {
    return Object.values(AccountCreationManualApprovalStatus).indexOf($) > -1;
}

export type DelayedWalletCreationResponse = {
    manualApprovalId: number;
    userId: number;
    status: AccountCreationManualApprovalStatus;
};

export function isDelayedWalletCreationResponse($: any): $ is DelayedWalletCreationResponse {
    return typeof $ === 'object'
        && $ !== null
        && isAccountCreationManualApprovalStatus($.status);
}

export type CreateIbanDto = {
    manualApproval?: DelayedWalletCreationResponse,
    account?: AccountResponse,
};

export enum CreateIbanResult {
    CREATED = 'CREATED',
    PENDING = 'PENDING',
}

@Injectable()
export class AccountService extends SocketNamespaceServiceBase {

    public newAccountAdded: EventEmitter<Account> = new EventEmitter<Account>();

    public totals: { EUR: number, USD: number } = {EUR: 0, USD: 0};

    protected readonly _accounts$ = new BehaviorSubject<null | Array<Account>>(null);
    protected readonly _selectedAccount$ = new BehaviorSubject<null | Account>(null);

    public constructor(
        protected readonly currencyService: CurrencyService,
        protected readonly httpClient: HttpClient,
        protected readonly authenticationService: AuthenticationService,
    ) {
        super('account');

        this.currencyService.currencies$.pipe(
            skip(1),
        ).subscribe(($currencies) => {
            this.onRateChange();
        });

        // Clear data every time user looses login credentials (logout)
        this.authenticationService.credentials$.pipe(
            filter($ => $ === null),
        ).subscribe(() => {
            if (this._accounts$.value !== null) {
                this._accounts$.next(null);
            }
        });
    }

    public account(id: number): Account | null {
        return this._accounts$.value && this._accounts$.value.find(a => a.id === id);
    }

    public accountDetails(id: string): Observable<AccountResponse | null> {
        return this.httpClient.get<AccountResponse | null>('/accounts/' + id).pipe(
            tap($acc => {
                this._selectedAccount$.next($acc ? this.addOrUpdate($acc) : null);
            })
        );
    }

    public getByCurrency(code: string): Account[] {
        return this._accounts$.value.filter(a => a.currencyCode === code);
    }

    public getAccountByCurrency(code: string): Account | null {
        return this._accounts$.value && this._accounts$.value.find(a => a.currencyCode === code);
    }

    public createAccount$($currencyCode: string): Observable<Account> {
        return this.httpClient.post<AccountResponse>('/accounts', {ccy: $currencyCode}).pipe(
            map($response => {
                // Will be updated by the socket, but to speed up things we can add this to the list.
                return this.addOrUpdate($response);
            }),
        );
    }

    public createBankDetails$($accountId: number): Observable<Account> {
        return this.httpClient.post<AccountResponse>('/accounts/create-bank-details', {accountId: $accountId}).pipe(
            map($response => {
                return this.addOrUpdate($response);
            }),
        );
    }

    // private listeners
    protected initListeners() {
        this.socket.on('update', ($accountId: number, $changed: AccountResponse) => {
            this.addOrUpdate($changed);
            this.onRateChange();
        });

        this.socket.on('new', ($account: AccountResponse) => {
            const acc = this.account($account.id);
            if (acc) {
                return;
            }

            this.addOrUpdate($account);
            this.onRateChange();
        });
    }

    private getData() {
        this.socket.once('dataR', ($accountsResponse: Array<Partial<AccountResponse>>) => {
            if (Array.isArray($accountsResponse)) {
                if ($accountsResponse.length > 0) {
                    // FIXME: This is poor implementation, since this._accounts$ emitts every time we are calling addOrUpdate!
                    for (const account of $accountsResponse) {
                        this.addOrUpdate(account);
                    }
                } else {
                    this._accounts$.next([]);
                }
                this.onRateChange();
            }
        });
        this.socket.emit('data');
    }

    protected onConnect() {
        this.getData();
    }

    private onRateChange() {
        if (!this._accounts$.value || this._accounts$.value.length === 0) {
            return;
        }

        // get the total in default currency
        let totalEur = new DecimalJS(0);
        let totalUsd = new DecimalJS(0);

        // group accounts
        for (const account of this._accounts$.value) {
            const eurCurrency = this.currencyService.currency('EUR');
            if (eurCurrency) {
                totalEur = totalEur.plus(
                    new DecimalJS(account.balance).times(ensureCurrencyRate(eurCurrency, 'EUR').rate).toNumber(),
                );
            }
            const usdCurrency = this.currencyService.currency('USD');
            if (usdCurrency) {
                totalUsd = totalUsd.plus(
                    new DecimalJS(account.balance).times(ensureCurrencyRate(usdCurrency, 'USD').rate).toNumber(),
                );
            }
        }

        this.totals.EUR = totalEur.toNumber();
        this.totals.USD = totalUsd.toNumber();
    }

    private addOrUpdate($accInfo: Partial<AccountResponse>): Account {
        const account: Account = {
            id: 0,
            userId: 0,
            currencyCode: 'EUR',
            type: AccountType.CUSTOMER_CRYPTO,
            label: null,
            balance: 0,
            balanceFrozen: 0,
            balanceOnHold: 0,
            totalFee: 0,
            totalReceived: 0,
            totalSent: 0,
            status: null,
            details: [],
            card: [],
            createdDate: new Date(),
            updatedDate: new Date(),
            ...accountResponseToAccount($accInfo),
        };

        if (Array.isArray(this._accounts$.value)) {
            const existingAccountIndex = this._accounts$.value.findIndex(c => c.id === account.id);
            if (existingAccountIndex < 0) {
                this._accounts$.next([
                    ...this._accounts$.value,
                    account,
                ]);
                this.newAccountAdded.emit(account);
            } else {
                this._accounts$.next([
                    ...this._accounts$.value.slice(0, existingAccountIndex),
                    account,
                    ...this._accounts$.value.slice(existingAccountIndex + 1),
                ]);
            }
        } else {
            this._accounts$.next([account]);
        }

        return account;
    }

    public get accounts$(): Observable<Array<Account> | null> {
        return this._accounts$;
    }

    public get bankAccounts$(): Observable<Array<Account & { type: AccountType.CUSTOMER_BANK }> | null> {
        return this.accounts$.pipe(
            map($accounts => {
                if (Array.isArray($accounts)) {
                    return $accounts.filter(($): $ is Account & { type: AccountType.CUSTOMER_BANK } => $.type === AccountType.CUSTOMER_BANK);
                }
                return null;
            }),
        );
    }

    public get cryptoAccounts$(): Observable<Array<Account & { type: AccountType.CUSTOMER_CRYPTO }> | null> {
        return this.accounts$.pipe(
            map($accounts => {
                if (Array.isArray($accounts)) {
                    return $accounts.filter(($): $ is Account & { type: AccountType.CUSTOMER_CRYPTO } => $.type === AccountType.CUSTOMER_CRYPTO);
                }
                return null;
            }),
        );
    }

    public get trustAccounts$(): Observable<Array<Account & { type: AccountType.CUSTOMER_CUSTODY }> | null> {
        return this.accounts$.pipe(
            map($accounts => {
                if (Array.isArray($accounts)) {
                    return $accounts.filter(($): $ is Account & { type: AccountType.CUSTOMER_CUSTODY } => $.type === AccountType.CUSTOMER_CUSTODY);
                }
                return null;
            }),
        );
    }

    public get accountsSnapshot(): Array<Account> | null {
        return this._accounts$.value;
    }

    public get selectedAccount$(): BehaviorSubject<Account | null> {
        return this._selectedAccount$;
    }
}
